import React, { useState } from 'react';
import { Typography, Checkbox, Link } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';

import { ConsentLabel } from './EmailAuthForm.styles';

import Button from '@/components/Button';
import { TextField } from '@/components';
import { validateEmail } from '@/utils/stringValidators';

const withConsentSchema = z.object({
  email: z.string().transform((v) => v.trim()).refine(
    (v) => validateEmail(v),
    { message: 'coverage.errors.email_error' },
  ),
  termsAccepted: z.boolean().refine((v) => v === true),
});

const emailOnlySchema = z.object({
  email: z.string().transform((v) => v.trim()).refine(
    (v) => validateEmail(v),
    { message: 'coverage.errors.email_error' },
  ),
});

type FormValues = z.infer<typeof withConsentSchema>;

export interface EmailAuthFormProps {
  /** Called with the trimmed email on valid submission. Throw to show the error message. */
  onSubmit: (email: string) => void | Promise<void>;
  loading?: boolean;
  /** Whether to show the terms checkbox. Defaults to true. */
  showConsent?: boolean;
  /** Translation keys — all have defaults matching Spark's existing keys. */
  i18nKeys?: {
    emailLabel?: string;
    emailPlaceholder?: string;
    continueButton?: string;
    submittingButton?: string;
    errorMessage?: string;
  };
}

const EmailAuthForm: React.FC<EmailAuthFormProps> = ({
  onSubmit,
  loading = false,
  showConsent = true,
  i18nKeys = {},
}) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState('');

  const emailLabel = i18nKeys.emailLabel ?? 'coverage.pre_onboarding.email_auth.email_label';
  const emailPlaceholder = i18nKeys.emailPlaceholder ?? 'coverage.pre_onboarding.email_auth.email_placeholder';
  const continueButton = i18nKeys.continueButton ?? 'coverage.pre_onboarding.email_auth.continue';
  const submittingButton = i18nKeys.submittingButton ?? 'coverage.pre_onboarding.email_auth.submitting';
  const errorMessage = i18nKeys.errorMessage ?? 'coverage.pre_onboarding.email_auth.error';

  const { handleSubmit, control, formState } = useForm<FormValues>({
    resolver: zodResolver(showConsent ? withConsentSchema : emailOnlySchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: '',
      termsAccepted: false,
    },
  });

  const onFormSubmit = async (data: FormValues) => {
    setSubmitError('');
    try {
      await onSubmit(data.email);
    } catch {
      setSubmitError(t(errorMessage));
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Controller
        name="email"
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            id="email"
            label={t(emailLabel)}
            inputProps={{
              placeholder: t(emailPlaceholder),
              autoCapitalize: 'none',
              autoCorrect: 'off',
              type: 'email',
              'data-testid': 'email-input',
            }}
            error={!!fieldState.error}
            helperText={fieldState.error ? t('coverage.errors.email_error') : undefined}
            required
          />
        )}
      />

      {showConsent && (
        <Controller
          name="termsAccepted"
          control={control}
          render={({ field }) => (
            <ConsentLabel
              label={(
                <Typography>
                  {t('coverage.pre_onboarding.email_auth.read_and_accept')}
                  {' '}
                  <Link
                    href={t('footer.privacy_notice.link')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('coverage.pre_onboarding.email_auth.privacy_policy')}
                  </Link>
                  {' '}
                  {t('coverage.pre_onboarding.email_auth.and')}
                  {' '}
                  <Link
                    href={t('footer.terms_of_use.link')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('coverage.pre_onboarding.email_auth.terms_of_use')}
                  </Link>
                  *
                </Typography>
              )}
              control={(
                <Checkbox
                  {...field}
                  checked={Boolean(field.value)}
                  data-testid="terms-checkbox"
                />
              )}
            />
          )}
        />
      )}

      <Button
        variant="contained"
        data-testid="continue-button"
        type="submit"
        fullWidth
        color="primary"
        disabled={!formState.isValid || loading}
        rounded
        style={{ borderRadius: 100 }}
      >
        {loading ? t(submittingButton) : t(continueButton)}
      </Button>

      {submitError && (
        <Typography color="error" data-testid="submit-error" style={{ marginTop: 8 }}>
          {submitError}
        </Typography>
      )}
    </form>
  );
};

export default EmailAuthForm;
